import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  CustomerSupport360,
  DeskDashboardSummary,
  DeskForm,
  DeskKbArticle,
  DeskKbCategory,
  DeskPriority,
  DeskStatus,
  DeskTeam,
  DeskTicket,
  DeskTicketDetail,
  DeskTicketRequest,
} from '@/types/desk';

export const deskApi = {
  dashboardSummary: () =>
    unwrap(api.get<ApiResponse<DeskDashboardSummary>>('/v1/desk/dashboard/summary')),

  support360: (customerId: number) =>
    unwrap(api.get<ApiResponse<CustomerSupport360>>(`/v1/desk/customers/${customerId}/support-360`)),

  listStatuses: () =>
    unwrap(api.get<ApiResponse<DeskStatus[]>>('/v1/desk/statuses')),

  createStatus: (body: Partial<DeskStatus>) =>
    unwrap(api.post<ApiResponse<DeskStatus>>('/v1/desk/statuses', body)),

  listPriorities: () =>
    unwrap(api.get<ApiResponse<DeskPriority[]>>('/v1/desk/priorities')),

  createPriority: (body: Partial<DeskPriority>) =>
    unwrap(api.post<ApiResponse<DeskPriority>>('/v1/desk/priorities', body)),

  listTeams: () =>
    unwrap(api.get<ApiResponse<DeskTeam[]>>('/v1/desk/teams')),

  createTeam: (body: Partial<DeskTeam>) =>
    unwrap(api.post<ApiResponse<DeskTeam>>('/v1/desk/teams', body)),

  listForms: () =>
    unwrap(api.get<ApiResponse<DeskForm[]>>('/v1/desk/forms')),

  listKbCategories: () =>
    unwrap(api.get<ApiResponse<DeskKbCategory[]>>('/v1/desk/kb/categories')),

  createKbCategory: (body: { name: string; parentCategoryId?: number }) =>
    unwrap(api.post<ApiResponse<DeskKbCategory>>('/v1/desk/kb/categories', body)),

  listKbArticles: () =>
    unwrap(api.get<ApiResponse<DeskKbArticle[]>>('/v1/desk/kb/articles')),

  createKbArticle: (body: { title: string; content: string; categoryId?: number; published?: boolean }) =>
    unwrap(api.post<ApiResponse<DeskKbArticle>>('/v1/desk/kb/articles', body)),

  listTickets: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<DeskTicket>>>('/v1/desk/tickets', { params })),

  getTicket: (id: number, viewerEmployeeId?: number) =>
    unwrap(api.get<ApiResponse<DeskTicketDetail>>(`/v1/desk/tickets/${id}`, {
      params: viewerEmployeeId != null ? { viewerEmployeeId } : undefined,
    })),

  createTicket: (body: DeskTicketRequest) =>
    unwrap(api.post<ApiResponse<DeskTicket>>('/v1/desk/tickets', body)),

  assignTicket: (id: number, assignedEmployeeId: number) =>
    unwrap(api.post<ApiResponse<DeskTicket>>(`/v1/desk/tickets/${id}/assign`, { assignedEmployeeId })),

  transitionTicket: (id: number, statusId: number) =>
    unwrap(api.post<ApiResponse<DeskTicket>>(`/v1/desk/tickets/${id}/transition`, { statusId })),

  addComment: (id: number, commentText: string, internalNote = false) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/desk/tickets/${id}/comments`, { commentText, internalNote })),

  submitCsat: (id: number, rating: number, feedback?: string) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/desk/tickets/${id}/csat`, { rating, feedback })),

  reports: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskReportsBundle>>('/v1/desk/reports')),

  listSlaPolicies: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskSlaPolicy[]>>('/v1/desk/setup/sla-policies')),

  listCategories: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskCategory[]>>('/v1/desk/setup/categories')),

  listAutomationRules: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskAutomationRule[]>>('/v1/desk/setup/automation-rules')),

  listInboxConversations: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskInboxConversation[]>>('/v1/desk/inbox/conversations')),

  listInboxMessages: (conversationId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/desk').DeskInboxMessage[]>>(
        `/v1/desk/inbox/conversations/${conversationId}/messages`,
      ),
    ),

  board: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskBoardColumn[]>>('/v1/desk/boards')),

  getMailbox: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskMailbox>>('/v1/desk/mailboxes')),

  getBusinessCalendar: () =>
    unwrap(
      api.get<ApiResponse<import('@/types/desk').DeskBusinessCalendar>>('/v1/desk/setup/business-calendar'),
    ),

  updateBusinessCalendar: (body: {
    name?: string;
    timezone: string;
    workingDays?: string[];
    workStart?: string;
    workEnd?: string;
  }) =>
    unwrap(
      api.put<ApiResponse<import('@/types/desk').DeskBusinessCalendar>>('/v1/desk/setup/business-calendar', body),
    ),

  listHolidays: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskHoliday[]>>('/v1/desk/setup/holidays')),

  addHoliday: (body: { holidayDate: string; name?: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/desk').DeskHoliday>>('/v1/desk/setup/holidays', body)),

  deleteHoliday: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/desk/setup/holidays/${id}`)),

  updateMailboxImap: (body: {
    imapHost?: string;
    imapPort?: number;
    imapUsername?: string;
    imapPassword?: string;
    syncEnabled?: boolean;
  }) => unwrap(api.put<ApiResponse<import('@/types/desk').DeskMailbox>>('/v1/desk/mailboxes/imap', body)),

  syncMailbox: () =>
    unwrap(api.post<ApiResponse<{ processed: number }>>('/v1/desk/mailboxes/sync')),

  listWorkflows: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskWorkflow[]>>('/v1/desk/workflows')),

  createWorkflow: (body: { name: string; active?: boolean }) =>
    unwrap(api.post<ApiResponse<import('@/types/desk').DeskWorkflow>>('/v1/desk/workflows', body)),

  listWorkflowTransitions: (workflowId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/desk').DeskWorkflowTransition[]>>(
        `/v1/desk/workflows/${workflowId}/transitions`,
      ),
    ),

  createWorkflowTransition: (
    workflowId: number,
    body: {
      fromStatusId?: number;
      toStatusId?: number;
      allowedRoleCode?: string;
      active?: boolean;
    },
  ) =>
    unwrap(
      api.post<ApiResponse<import('@/types/desk').DeskWorkflowTransition>>(
        `/v1/desk/workflows/${workflowId}/transitions`,
        body,
      ),
    ),

  listCannedResponses: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskCannedResponse[]>>('/v1/desk/canned-responses')),

  createCannedResponse: (body: {
    title: string;
    shortcutCode?: string;
    bodyText: string;
    categoryCode?: string;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<import('@/types/desk').DeskCannedResponse>>('/v1/desk/canned-responses', body)),

  updateCannedResponse: (
    id: number,
    body: {
      title: string;
      shortcutCode?: string;
      bodyText: string;
      categoryCode?: string;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<import('@/types/desk').DeskCannedResponse>>(`/v1/desk/canned-responses/${id}`, body)),

  deleteCannedResponse: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/desk/canned-responses/${id}`)),

  listAttachments: (ticketId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/desk').DeskAttachment[]>>(`/v1/desk/tickets/${ticketId}/attachments`),
    ),

  addAttachment: (
    ticketId: number,
    body: { fileUrl: string; fileName?: string; mimeType?: string; fileSize?: number },
  ) =>
    unwrap(
      api.post<ApiResponse<import('@/types/desk').DeskAttachment>>(
        `/v1/desk/tickets/${ticketId}/attachments`,
        body,
      ),
    ),

  replyInboxConversation: (conversationId: number, messageContent: string) =>
    unwrap(
      api.post<ApiResponse<import('@/types/desk').DeskInboxMessage>>(
        `/v1/desk/inbox/conversations/${conversationId}/reply`,
        { messageContent },
      ),
    ),

  exportReport: (format: 'CSV' | 'PDF' = 'CSV') =>
    api.get('/v1/desk/reports/export', { params: { format }, responseType: 'blob' }),
};
